/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef } from 'react';
import { useAppSelector, useAppDispatch } from '@doda/pc/src/redux/hooks';
import classNames from 'classnames';
import { Box } from '@doda/io-react/common';
import {
  TAB_JOBDETAIL,
  TAB_PHOTOS,
  TAB_PR,
} from '@doda/common/src/constants/properties/const';
import dynamic from 'next/dynamic';
import { shouldShowDirectMessageButton } from '@doda/common/src/logics/jobSearchDetail/shouldShowDirectMessageButton';
import {
  getJobOfferKindIsDirect,
  getJobOfferKindIsWithoutApplyButton,
} from '@doda/common/src/logics/jobSearchDetail/getJobOfferKind';
import { getApplyButtonAbTestSuffix } from '@doda/common/src/logics/jobSearchDetail/getApplyButtonAbTestSuffix';
import styles from './index.module.scss';
import { ApplyButton } from '../ApplyButton';
import { AskCompanyButton, AskCompanyButtonProps } from '../AskCompanyButton';
import { Tab } from './Tab';
import { useCountView, useTab } from './hooks/useTab';
import { Tab as TabType } from '../../types/tab';
import { PickupProps } from '../Body/Pickup';
import { JobDetail, JobDetailProps } from '../Body/JobDetail';
import { PublicationPhotoContent } from '../Body/PublicationPhotoArea';
import { FavoriteButton, FavoriteButtonProps } from '../FavoriteButton';
import { useApply, Arg as ApplyParamsArg } from '../../hooks/useApply';
import { FooterApplyArea } from '../Body/JobDetail/FooterApplyArea';
import {
  corporateClientPhotoSelector,
  corporateClientPhotosLatestUpdateDateSelector,
} from '../../redux/features/jobSearchDetail';
import {
  measureApply,
  getOfferKindText,
  measureBookmarkSaved,
  measureBookmark,
} from '../../logics/analytics';

const Pickup = dynamic(() =>
  import('../Body/Pickup').then((module) => ({ default: module.Pickup }))
);

/**
 * タブエリアのプロパティ
 */
interface TabAreaProps {
  /** 企業に問い合わせるボタンのプロパティ */
  askCompanyButtonProps: Omit<
    AskCompanyButtonProps,
    'jid' | 'onClick' | 'isApplied' | 'size'
  >;
  /** 求人種別 */
  jobOfferKind: string | undefined;
  /** 気になるボタンのプロパティ */
  favoriteButtonProps: Omit<FavoriteButtonProps, 'jid' | 'size' | 'onClick'>;
  /** 求人ID */
  jid: string;
  /** 初期表示時のタブ */
  initialTab: TabType;
  /** 原稿種別 */
  wcnsId: string;
  /** Pickupコンポーネントのprops */
  pickupProps: Omit<PickupProps, 'wcnsId' | 'initialTab' | 'jid' | 'isApplied'>;
  /** JobDetailコンポーネントのprops */
  jobDetailProps: JobDetailProps;
  /** 応募用のプロパティ */
  applyParams: Omit<
    ApplyParamsArg,
    'jid' | 'jobOfferKind' | 'initialTab' | 'position'
  >;
  /** メッセージ送信可能か */
  isCanSent: boolean;
  /** 応募済みか */
  isApplied: boolean;
  /** 応募ボタンUI変更ABテストグループ番号 */
  applyButtonChangeAbTestGroupNo?: string;
}

/**
 * 応募ボタンと企業に問い合わせるボタンを表示するコンポーネント。
 */
export const TabArea: React.FC<TabAreaProps> = ({
  askCompanyButtonProps,
  jobOfferKind = '1',
  favoriteButtonProps,
  jid,
  initialTab,
  wcnsId,
  pickupProps,
  jobDetailProps,
  applyParams,
  isCanSent,
  isApplied,
  applyButtonChangeAbTestGroupNo,
}) => {
  const { createTabItems, currentTabName } = useTab({
    initialTab,
    jid,
    wcnsId,
  });
  useCountView({
    initialTab,
    jid,
    isApplied,
  });

  const tabItems = createTabItems(isApplied);
  const hasApplyButton = !getJobOfferKindIsWithoutApplyButton(jobOfferKind);
  const isDirect = getJobOfferKindIsDirect(jobOfferKind);
  const isShowMessageButton = shouldShowDirectMessageButton(
    isDirect,
    isCanSent
  );

  const publicationPhotos = useAppSelector(corporateClientPhotoSelector);
  const latestUpdateDate = useAppSelector(
    corporateClientPhotosLatestUpdateDateSelector
  );

  return (
    <Box boxShadow="blue">
      <div className={styles['jobSearchDetail-tabArea']}>
        <Tab tabItems={tabItems} />
        <TabAreaButtons
          jobOfferKind={jobOfferKind}
          askCompanyButtonProps={{ ...askCompanyButtonProps }}
          favoriteButtonProps={{ ...favoriteButtonProps }}
          applyParams={{
            ...applyParams,
            initialTab,
            jobOfferKind,
          }}
          isCanSent={isCanSent}
          isApplied={isApplied}
          initialTab={initialTab}
          jid={jid}
          position="top"
          hasApplyButton={hasApplyButton}
          applyButtonChangeAbTestGroupNo={applyButtonChangeAbTestGroupNo}
        />
      </div>
      <div className={styles['jobSearchDetail-tabArea__container']}>
        {currentTabName === TAB_PR && (
          <Pickup
            {...pickupProps}
            wcnsId={wcnsId}
            jid={jid}
            initialTab={initialTab}
            isApplied={isApplied}
          />
        )}
        {currentTabName === TAB_JOBDETAIL && (
          <JobDetail
            wcnsId={wcnsId}
            initialTab={initialTab}
            isApplied={isApplied}
            {...jobDetailProps}
          />
        )}
        {currentTabName === TAB_PHOTOS && publicationPhotos && (
          <PublicationPhotoContent
            publicationPhotos={publicationPhotos}
            latestUpdateDate={latestUpdateDate}
          />
        )}
        {(hasApplyButton || isShowMessageButton) && (
          <div
            className={classNames(
              styles['jobSearchDetail-tabArea__footerArea'],
              {
                [styles['jobSearchDetail-tabArea__footerArea--tabPr']]:
                  currentTabName === TAB_PR,
              }
            )}
          >
            {hasApplyButton && (
              <FooterApplyArea
                jid={jid}
                jobOfferKind={jobOfferKind}
                initialTab={initialTab}
                wcnsId={wcnsId}
                applyButtonChangeAbTestGroupNo={applyButtonChangeAbTestGroupNo}
                {...applyParams}
              />
            )}
            {isShowMessageButton && (
              <AskCompanyButton
                jid={jid}
                isApplied={isApplied}
                size="L"
                {...askCompanyButtonProps}
              />
            )}
          </div>
        )}
      </div>
      <div
        className={`${styles['jobSearchDetail-tabArea']} ${styles['jobSearchDetail-tabArea--upsideDown']}`}
      >
        <Tab isUpsideDown tabItems={tabItems} />
        <TabAreaButtons
          jobOfferKind={jobOfferKind}
          askCompanyButtonProps={{ ...askCompanyButtonProps }}
          favoriteButtonProps={{ ...favoriteButtonProps }}
          applyParams={{
            ...applyParams,

            initialTab,
            jobOfferKind,
          }}
          isCanSent={isCanSent}
          isApplied={isApplied}
          initialTab={initialTab}
          jid={jid}
          position="bottom"
          hasApplyButton={hasApplyButton}
          applyButtonChangeAbTestGroupNo={applyButtonChangeAbTestGroupNo}
        />
      </div>
    </Box>
  );
};

/**
 * タブエリア内のボタン群のプロパティ
 */
interface TabAreaButtonsProps {
  /** 求人種別 */
  jobOfferKind: string;
  /** 企業に問い合わせるボタンのプロパティ */
  askCompanyButtonProps: Omit<
    AskCompanyButtonProps,
    'isApplied' | 'onClick' | 'jid' | 'size'
  >;
  /** 気になるボタンのプロパティ */
  favoriteButtonProps: Omit<FavoriteButtonProps, 'size' | 'onClick' | 'jid'>;
  /** 応募用のプロパティ */
  applyParams: Omit<ApplyParamsArg, 'jid' | 'position'>;
  /** メッセージ送信可能か */
  isCanSent: boolean;
  /** 応募済みか */
  isApplied: boolean;
  /* 初期表示のタブ */
  initialTab: TabType;
  /* 求人ID */
  jid: string;
  /* ボタン位置 */
  position: 'top' | 'bottom';
  /* 応募ボタンありフラグ */
  hasApplyButton: boolean;
  /** 応募ボタンUI変更ABテストグループ番号 */
  applyButtonChangeAbTestGroupNo?: string;
}

/**
 * 求人情報のタブエリアとボタン群
 */
const TabAreaButtons: React.FC<TabAreaButtonsProps> = ({
  jobOfferKind,
  askCompanyButtonProps,
  favoriteButtonProps,
  applyParams,
  isCanSent,
  isApplied,
  initialTab,
  jid,
  position,
  hasApplyButton,
  applyButtonChangeAbTestGroupNo,
}) => {
  const measuredPosition = position === 'top' ? 'Top' : 'Bottom';
  const { apply } = useApply({ ...applyParams, jid, position });
  const dispatch = useAppDispatch();
  const isDirect = getJobOfferKindIsDirect(jobOfferKind);
  const isShowMessageButton = shouldShowDirectMessageButton(
    isDirect,
    isCanSent
  );
  const onClickTabFavorite = () => {
    dispatch(
      measureBookmark({
        initialTab,
        jid,
      })
    );
  };

  const onClickTabBookmarkSaved = () => {
    dispatch(
      measureBookmarkSaved({
        position: measuredPosition,
        initialTab,
        jid,
      })
    );
  };

  const abTestSuffix = getApplyButtonAbTestSuffix(
    applyButtonChangeAbTestGroupNo ?? '0'
  );
  const isApplyClickedRef = useRef(false);
  const onClickTabApply = () => {
    if (isApplyClickedRef.current) return;
    isApplyClickedRef.current = true;
    dispatch(
      measureApply({
        position: measuredPosition,
        apply: getOfferKindText(jobOfferKind),
        initialTab,
        jid,
        abTestSuffix,
      })
    );
    apply();
  };

  // DODASITE-25064 応募ボタンUI変更ABテスト テストパターン２、３の場合は幅を拡大
  const getApplyButtonWidth = (): number | undefined => {
    if (
      applyButtonChangeAbTestGroupNo === '2' ||
      applyButtonChangeAbTestGroupNo === '3'
    ) {
      return jobOfferKind === '0' ? 222 : 194;
    }
    return 152;
  };

  return (
    <div className={styles['jobSearchDetail-tabArea__buttons']}>
      {position === 'top' && (
        <>
          {hasApplyButton && (
            <ApplyButton
              jobOfferKind={jobOfferKind}
              width={getApplyButtonWidth()}
              size="S"
              onClick={onClickTabApply}
              abTestGroupNo={applyButtonChangeAbTestGroupNo}
            />
          )}
          {isShowMessageButton && (
            <AskCompanyButton
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...askCompanyButtonProps}
              width={152}
              size="S"
              isApplied={isApplied}
              jid={jid}
            />
          )}
        </>
      )}
      {!isApplied && (
        <FavoriteButton
          {...favoriteButtonProps}
          width="152px"
          size="S"
          onClickFavorite={onClickTabFavorite}
          onClickSaved={onClickTabBookmarkSaved}
          jid={jid}
        />
      )}
    </div>
  );
};
